
// ── Integration scaffolding ──
export type SyncStatus = "not_synced" | "pending" | "synced" | "failed";
export type ExternalSource = "GHL" | "BoxHero" | "Google Calendar" | "QuickBooks" | "Outlook" | "Salesforce" | "Jobber" | "Custom";

// ── Import / Migration metadata (additive — beta-safe Jobber bridge) ──
// Anchor: mem://features/jobber-import-bridge (beta)
export type SourceSystem = "koredor" | "jobber" | "external_csv" | "manual_import";
export type MigrationStatus = "needs_review" | "approved" | "rejected";
export type ImportReadinessMode = "native_managed" | "external_managed";
export type MigrationBypassStage = "build_review" | "permitting" | "ordering";
export type ReviewFlag =
  | "missing_address"
  | "missing_scope"
  | "missing_install_type"
  | "missing_schedule"
  | "missing_offer_comp"
  | "missing_photos_spec"
  | "ambiguous_line_items"
  | "low_confidence_mapping"
  | "duplicate_possible";

export interface ImportSourceMeta {
  sourceSystem: SourceSystem;
  /**
   * Explicit readiness ownership contract for imported work:
   * - native_managed: Koredor owns upstream readiness gates.
   * - external_managed: external system owns upstream readiness gates.
   */
  readinessMode?: ImportReadinessMode;
  /** Stable id from the originating system (e.g. Jobber project id). */
  externalProjectId: string;
  externalCustomerId?: string;
  importedAt: string;
  importBatchId: string;
  migrationStatus: MigrationStatus;
  reviewFlags: ReviewFlag[];
  /** Free-form opaque pointer back to the source payload (URL / row hash). */
  rawSourceRef?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedReason?: string;
  /** Confidence score 0–1 from mapper heuristics. */
  mappingConfidence?: number;
  /**
   * Stages explicitly bypassed during migration review.
   * Absence of this field (legacy) = all three stages bypassed.
   */
  bypassedStages?: MigrationBypassStage[];
}
export type DataOrigin = "manual" | "system" | "external";

export interface ExternalRef {
  externalId?: string;
  externalSource?: ExternalSource;
  syncStatus?: SyncStatus;
  lastSyncedAt?: string;
}

// ── Job stages & statuses ──
export type JobStage =
  | "Sale Closed"
  | "Review and Validation"
  | "Review Complete"
  | "Ready for Ordering"
  | "Ordered"
  | "Permit and Order Pending"
  | "Ready for Scheduling"
  | "Scheduled Tentative"
  | "Scheduled Confirmed"
  | "Ready for Dispatch"
  | "Available for Contractor Assignment"
  | "Assigned and Dispatched"
  | "In Progress"
  | "QA Review"
  | "Completed"
  | "Issue";

export type PermitStatus = "Not Required" | "Submitted" | "Approved" | "Revision Needed";
export type DetailedPermitStage =
  | "Not Required"
  | "Permit Required"
  | "Preparing Documents"
  | "Submitted"
  | "Under Review"
  | "Revision Requested"
  | "Resubmitted"
  | "Approved"
  | "Closed";

export interface PermitDetail {
  permitType: string;
  municipality: string;
  permitNumber?: string;
  permitCost?: number;
  submittedDate: string;
  detailedStage: DetailedPermitStage;
  lastUpdate: string;
  permitNotes: string;
  assignedCoordinator: string;
  blockers: string;
  targetApprovalDate: string;
  /**
   * Append-only ledger of municipality touchpoints (Phase 1A).
   * See `src/data/permitting/municipalityLog.ts` — never push directly;
   * always go through `appendMunicipalityInteraction()`.
   */
  municipalityLog?: import("./permitting/municipalityLog").MunicipalityInteraction[];
  /**
   * Per-job requirement completion ledger (Phase 1B). Items absent from
   * the ledger render as "pending". Mutations flow through
   * `setRequirementStatus()` in `requirementsChecklist.ts`.
   */
  requirementChecklist?: import("./permitting/requirementsChecklist").RequirementChecklistEntry[];
  /**
   * Structured revision packet (Phase 1D). Optional — only present after
   * a coordinator opens one via `setRevisionRequestMetadata()`. Items
   * are toggled via `setRevisionItemResolved()`. See
   * `src/data/permitting/revisionRequest.ts`.
   */
  revisionRequest?: import("./permitting/revisionRequest").RevisionRequest;
  /**
   * Structured post-approval inspection workflow (Phase 1E). Optional —
   * only present once a coordinator opens it via `addInspection()`.
   * See `src/data/permitting/inspectionLifecycle.ts`.
   */
  inspectionLifecycle?: import("./permitting/inspectionLifecycle").InspectionLifecycle;
  /**
   * Structured permit-closure coordination (Phase 1F). Optional — only
   * present once a coordinator opens it via `openPermitCloseout()`.
   * See `src/data/permitting/permitCloseout.ts`.
   */
  permitCloseout?: import("./permitting/permitCloseout").PermitCloseout;
  /**
   * NTBO / permit prep workflow data. Optional — populated when job is in
   * "Preparing Documents" stage. See `NtboTrackerPanel`.
   */
  prep?: import("@/components/permitting/NtboTrackerPanel").PermitPrep;
  /**
   * Property Appraiser parcel identifier — required before advancing from "Preparing Documents".
   */
  parcelNumber?: string;
  /**
   * Whether the job scope includes a second bathroom — controls conditional
   * display of the "Completed Drawing/Floor Plan (Second Bathroom)" checklist item.
   */
  hasSecondBathroom?: boolean;
  /**
   * Fine-grained sub-state within the "Preparing Documents" permit stage.
   * Derived from `PermitPrep.signatureRequest.status` + `notarizationRequired`
   * + `notarizationComplete` and persisted to `permitDetail.ntboSubStatus`
   * by NtboTrackerPanel on save.
   */
  ntboSubStatus?: NtboSubStatus;
  /**
   * Internal handoff owner for the permit prep workflow.
   * Auto-derived from the signature workflow state and persisted to
   * `permitDetail.assignedTo` by NtboTrackerPanel on every save.
   */
  assignedTo?: PermitAssignee;
}

/**
 * Fine-grained sub-state within the "Preparing Documents" permit stage.
 * Derived from `PermitPrep.signatureRequest.status` + `notarizationRequired`
 * + `notarizationComplete` and persisted to `permitDetail.ntboSubStatus`
 * by NtboTrackerPanel on save.
 */
export type NtboSubStatus =
  | "NotStarted"
  | "AwaitingHomeownerSignature"
  | "AwaitingNotarization"
  | "ReadyForSubmission";

/**
 * Internal handoff owner for the permit prep workflow.
 * Auto-derived from the signature workflow state and persisted to
 * `permitDetail.assignedTo` by NtboTrackerPanel on every save.
 */
export type PermitAssignee = "Abby" | "Debora" | "Wesley" | "Homeowner";

export type HomeType = "SFH" | "CONDO" | "MOBILE" | "Manufactured" | "Other";
export type NtboStatus = "Not Started" | "Preparing" | "Submitted to Municipality" | "Approved" | "PDF Generated" | "Other";
export type PlumbingPermitStatus = "Not Started" | "In Progress" | "Completed" | "Items Needed" | "Other";
/** @deprecated Use planningStatus instead */
export type OrderStatus = "Not Ordered" | "Ordered" | "Confirmed" | "ETA Received";
/** @deprecated Use materialReadiness instead */
export type MaterialsStatus = "Not Ready" | "Ready" | "Missing Items";

// ── Separated Material System Statuses ──
export type ProcurementStatus = "Not Ordered" | "Ordered" | "Confirmed" | "In Transit" | "Delivered";
export type WarehouseStatus = "Not Received" | "Partially Received" | "Received" | "Staged" | "Loaded" | "Missing Items";
export type InventoryItemStatus = "In Stock" | "Reserved" | "Allocated" | "Low Stock";

export type InventoryAssignmentType = "Assigned" | "Stock";
export type MaterialLifecycleStage = "Ordered" | "Received" | "Assigned" | "Installed";

// ── Warehouse Workflow Material State System ──
export type MaterialState = "Not Ordered" | "Ordered" | "Received" | "Allocated" | "Installed";

export type MaterialEventAction =
  | "ordered"
  | "received"
  | "allocated"
  | "loadout_confirmed"
  | "installed"
  | "photo_attached"
  | "materials_reviewed"
  | "materials_ready";

export interface MaterialEvent {
  id: string;
  jobId: string;
  sku: string;
  action: MaterialEventAction;
  timestamp: string;
  performedBy: string;
  notes?: string;
  photoUrl?: string;
  previousState?: MaterialState;
  newState?: MaterialState;
}
export type RescissionStatus = "In Rescission" | "Cleared for Production" | "Not Applicable";

export type MaterialReadiness =
  | "Not Ordered"
  | "Ordered"
  | "Partially Received"
  | "Fully Received"
  | "Staged for Job"
  | "Loaded for Crew"
  | "Missing Items"
  | "Field Purchase Required";
export type DispatchStatus =
  | "Not Ready"
  | "Ready for Dispatch"
  | "Sent to Contractor Network"
  | "Awaiting Response"
  | "Accepted"
  | "Rejected"
  | "Assigned"
  | "Closed";
export type AssignmentType = "Internal Crew" | "Subcontractor" | "Unassigned";
export type IssueSeverity = "Low" | "Medium" | "Urgent";
export type IssueSource = "Field Crew" | "Subcontractor" | "Sales Rep" | "Admin" | "Customer" | "QA Inspector" | "Warehouse";
export type IssueType =
  | "Install Issue"
  | "Material Issue"
  | "Customer Complaint"
  | "Scheduling Issue"
  | "Permit Issue"
  | "Ordering Issue"
  | "Contractor Issue"
  | "Other";
export type IssueStatus = "Open" | "In Progress" | "Resolved" | "Closed";
export type ContractorResponseStatus = "Accepted" | "Rejected" | "Bid Submitted" | "No Response" | "Pending" | "Viewed";
export type CloseoutStatus =
  | "Not Started"
  | "Install Complete"
  | "QA Passed"
  | "Final Photos Complete"
  | "Issues Resolved"
  | "Ready for Closeout"
  | "Closed";

export interface JobAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  /** Optional geocoded coords. Used by installer check-in distance display
   *  and backend trust scoring. Absent on legacy records. */
  lat?: number;
  lng?: number;
}

export interface JobPhotoGroup {
  id: string;
  label: string;
  photoRefs: string[];
  createdAt: string;
}

export interface JobMetadataCloseoutSignoff {
  signerType?: "homeowner" | "client" | "other";
  signerName?: string;
  notes?: string | null;
  approved?: boolean;
  signatureDataUrl?: string;
  signaturePaths?: Array<Array<{ x: number; y: number }>>;
  signedAt?: string;
  capturedBy?: string;
  updatedAt?: string;
}

export interface UnmappedSkuReviewItem {
  sku: string;
  itemName: string;
  proposedSku: string;
}

export interface Job {
  id: string;
  /** Human-readable job number (e.g. "J-1042"). Mapped from job_number in DB. */
  jobNumber?: string;
  organizationId?: string;
  /** Mapped from created_at in DB. */
  createdAt?: string;
  updatedAt?: string;
  customerName: string;
  address: JobAddress;
  phone: string;
  email: string;
  secondaryContactName: string;
  secondaryContactPhone: string;
  secondaryContactEmail: string;
  projectType: ProjectType;
  assignedCrew: string;
  /**
   * Optional crew assignment (P9 pilot, behind FEATURE_FLAGS.crewInsightsEnabled).
   * Informational only — never influences dispatch, scoring, or gating.
   */
  crewId?: string;
  assignmentType: AssignmentType;
  /**
   * Phase 2 — Final Fulfillment Bridge. Optional explicit override of the
   * resolved fulfillment mode. Pure metadata; resolver derives from
   * assignmentType + offers when absent. See `getFulfillmentMode()`.
   */
  fulfillmentMode?: "internal" | "marketplace" | "unassigned";
  scheduledInstallDate: string;
  /**
   * Number of contiguous crew-shifts this install consumes (1 shift = 1 crew-day).
   * Optional for back-compat — engine treats undefined as 1.
   * Mutated only via the Extend Job flow on the Plan grid; spans forward
   * from `scheduledInstallDate` skipping weekends.
   */
  scheduledShifts?: number;
  timeSlot?: string;
  permitRequired: boolean;
  permitStatus: PermitStatus;
  /** @deprecated Use planningStatus for order tracking */
  orderStatus: OrderStatus;
  /** @deprecated Use materialReadiness for material tracking */
  materialsStatus: MaterialsStatus;
  dispatchStatus: DispatchStatus;
  contractorAssignmentStatus: string;
  currentStage: JobStage;
  notes: string;
  issueFlag: boolean;
  scopeSummary: string;
  description?: string;
  payoutAmount: number;
  offerCompensationConfig?: {
    payoutOption: "fixed_payout" | "revenue_share";
    revenueShareMode?: "fixed_amount" | "percentage";
    fixedAmount?: number;
    percentageOfNet?: number;
    sourceMode?: "manual" | "percent" | "suggested";
    incentives?: Array<{
      type: "five_star" | "other";
      amount: number;
      label?: string;
      updatedAt?: string;
    }>;
    updatedAt?: string;
  };
  /** @deprecated Display/back-compat — name-keyed. New logic must use `assignedSubcontractorId`. */
  assignedSubcontractor: string;
  /** Canonical contractor identity for assignment. Additive, populated alongside the legacy name string. */
  assignedSubcontractorId?: string;
  targetInstallDate: string;
  activityHistory: ActivityEntry[];
  photos: string[];
  photoGroups?: JobPhotoGroup[];
  qaSubmitted: boolean;
  materialReadiness: MaterialReadiness;
  materialReadinessNotes: string;
  missingItemsList: string;
  // Separated material statuses
  procurementStatus: ProcurementStatus;
  warehouseStatus: WarehouseStatus;
  // Multi-tenant scaffolding
  branch: string;
  region: string;
  salesRep: string;
  // Permit detail
  permitDetail?: PermitDetail;
  // Permit tracker fields (from CSV tracking sheet)
  ntboStatus?: string;
  homeType?: string;
  plumbingPermitStatus?: string;
  parcelNumber?: string;
  permitExpectationDate?: string;
  drawingFloorPlanCompleted?: boolean;
  secondBathroomDrawingCompleted?: boolean;
  readyForInspection?: boolean;
  homeownerAvailableDate?: string;
  inspectionConfirmed?: boolean;
  // Order detail
  orderDetail?: OrderLineItem[];
  // Closeout
  closeoutStatus: CloseoutStatus;
  finalDocumentationComplete: boolean;
  customerSignoff: boolean;
  closeoutNotes: string;
  closeoutSignoff?: JobMetadataCloseoutSignoff;
  // Inventory
  materialsList?: MaterialLineItem[];
  fieldMaterials?: FieldMaterialItem[];
  /** Imported material SKUs awaiting warehouse review/matching before becoming catalog canonical. */
  unmappedSkusForReview?: UnmappedSkuReviewItem[];
  // Financials
  financials?: JobFinancials;
  // Marketing
  marketing?: MarketingAttribution;
  // ── Integration-ready fields ──
  externalRef?: ExternalRef;
  origin?: DataOrigin;
  // Scheduling time detail
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  timezone?: string;
  externalCalendarEventId?: string;
  // Project configuration / quote
  projectConfig?: ProjectConfiguration;
  // Rescission tracking
  rescissionStatus: RescissionStatus;
  rescissionEndsAt?: string;
  convertedFromQuoteId?: string;
  /**
   * JobSpec binding — execution source of truth.
   * Bound at job creation; immutable thereafter. See src/data/jobSpec.ts.
   */
  boundJobSpec?: import("./jobSpec").BoundJobSpec;
  /**
   * Flat checklist written by the installer portal.
   * Keys: `"{phaseName}:{itemIndex}"` → boolean.
   * Read-only in CCC — used as a fallback to derive phase completion when
   * job_spec_executions has no entry for the job.
   */
  _portalChecklist?: Record<string, boolean>;
  /**
   * Dispatch / execution lock. Set by markJobReady() once canonical readiness
   * AND assignment-time gates (Pay Profile) all pass. One-way in v1: cannot
   * be unset, no admin override path. `factorsAtLock` records what was true
   * at lock time so post-lock regressions remain auditable.
   * See src/data/executionReadyLock.ts.
   */
  executionReady?: {
    markedAt: string;
    markedBy: string;
    factorsAtLock: string[];
  };
  /** Contract document stored from quote approval */
  contractDocument?: import("./quoteTypes").ContractDocument;
  /** Change orders tied to this job's contract */
  changeOrders?: import("./changeOrderTypes").ChangeOrder[];
  /** Scope locked after intake approval — changes require change order */
  scopeLocked?: boolean;
  // ── Lifecycle backlinks (additive, optional) ──
  /** Originating Opportunity (set on close, or runtime-derived in AppDataContext when unambiguous). */
  opportunityId?: string;
  /** Originating sold AppointmentResult (set on close, or runtime-derived when exactly one match exists). */
  appointmentResultId?: string;
  /** Intake review approved — job cleared for material planning */
  intakeApproved?: boolean;
  /** Job-specific installer portal checklist authored by Koredor App. */
  checklist?: JobChecklist;
  /** Approved for ordering — materials validated via Build Review, sourced from POS */
  approvedForOrdering?: boolean;
  /** Hold status from intake review */
  holdStatus?: string;
  holdReason?: string;
  /** Planning status tracking the post-approval workflow */
  planningStatus?: "In Planning" | "Ready for Ordering" | "Sent to Ordering" | "Ordered" | "Awaiting Delivery" | "Receiving" | "Staged" | "Ready for Install";
  /** Order type preference set during planning */
  orderType?: "Batch" | "Emergency";
  /** Selected vendor during planning */
  selectedVendor?: string;
  /** Selected vendor type during planning */
  selectedVendorType?: string;
  /** Scheduling status tracking the post-warehouse workflow */
  schedulingStatus?: "Not Ready" | "Unscheduled" | "Scheduled" | "Crew Assigned" | "Confirmed";
  /** Install readiness — computed from material allocations */
  installReadiness?: "Not Ready" | "Ready";
  /** Job-level material status for tracking */
  jobMaterialStatus?: "Not Ordered" | "Ordered" | "Partially Received" | "Fully Received" | "Staged";
  /** Install execution status — linear progression */
  installStatus?: "Not Started" | "Scheduled" | "En Route" | "On Site" | "In Progress" | "Install Complete";
  /** QA status — post-install only */
  qaStatus?: "Not Started" | "In Progress" | "Submitted" | "Passed" | "Failed";
  // ── Review & Referral Flywheel ──
  reviewSent?: boolean;
  reviewCompleted?: boolean;
  reviewSatisfied?: boolean | null; // null = not yet asked, true = satisfied, false = not satisfied
  reviewSentAt?: string;
  referralRequested?: boolean;
  referralReceived?: boolean;
  referralRequestedAt?: string;
  googleReviewUrl?: string;
  contractorRatingSent?: boolean;
  contractorRatingUrl?: string;
  contractorRatingAt?: string;
  contractorRatingCompleted?: boolean;
  // ── Site Readiness ──
  /** Structured site readiness — optional for migration safety */
  siteReadiness?: SiteReadinessInfo;
  // ── Build Review ──
  /** Whether build review has been approved */
  buildReviewApproved?: boolean;
  /** Build review status for granular tracking */
  buildReviewStatus?: "not_started" | "pending" | "approved" | "rejected";
  /** Build review step checklist authored in Koredor App. Keys: step name → boolean. */
  buildReviewChecklist?: Record<string, boolean>;
  /** QA reviewer checklist. Keys: step name → boolean. */
  qaReviewerChecklist?: Record<string, boolean>;
  // ── Multi-Tenant Ownership ──
  /** Tenant/company this job belongs to. Optional for migration safety. */
  tenantId?: string;
  // ── P12.4 — Project Review Gate ──
  /**
   * Project review state. Crew/installer execution surfaces are gated on
   * `status === "approved"`. System estimate is never overwritten — overrides
   * live in the append-only PlanningOverrideArchive (linked via archiveEntryId).
   */
  planningReview?: import("./planningReview/types").JobPlanningReview;
  // ── Phase 1a — Network Execution Truth Layer (additive) ──
  /**
   * Project Owner contractor id. Distinct from the executor pointer
   * (`assignedSubcontractorId`). Backfilled to `CCC_OWNER_CONTRACTOR_ID`
   * for every existing job. Required semantically; optional in the type
   * for migration safety only.
   * Anchor: mem://architecture/network-execution-phase-1a
   */
  ownerContractorId?: string;
  /**
   * Append-only history of executor swaps. Phase 1b extends
   * `assignContractor()` to push entries here on every A→B / A→null /
   * null→A transition. Phase 1a leaves it as an empty array on backfill.
   */
  installerChange?: Array<{
    fromContractorId: string | null;
    toContractorId: string | null;
    at: string;
    reason: string;
    actor: string;
  }>;
  // ── Import / Migration metadata (beta-safe Jobber bridge) ──
  /**
   * Present only on jobs created via an import path (Jobber, CSV, manual).
   * Native Koredor quote→job flow MUST leave this undefined.
   * Visibility/dispatch surfaces gate on isMigrationApproved() — non-approved
   * imports are hidden from opportunities, Smart Dispatch, scheduling, offers.
   * See: src/data/import/migrationVisibilityGuard.ts
   */
  sourceMeta?: ImportSourceMeta;
  /**
   * Detailed parsed migration payload for imported jobs. Holds rich nested
   * objects from the migration JSON (shower system, installation, plumbing,
   * doors, fixtures, vanity, electrical, toilet, scope, job metadata, etc.)
   * so UI surfaces can render full spec without re-parsing raw source.
   */
  migrationImportDetail?: import("./import/migrationTypes").MigrationImportDetail;
}

export interface SiteReadinessInfo {
  accessConfirmed: boolean;
  utilitiesReady: boolean;
  customerPrepared: boolean;
  notes?: string;
}

// ── Canonical Project Types ──
export type ProjectType =
  | "Shower Remodel"
  | "Shower Remodel (Wet Space Only)"
  | "Bathtub Remodel"
  | "Bathtub Remodel (Wet Space Only)"
  | "Tub to Shower Conversion"
  | "Tub to Shower Conversion (Wet Space Only)"
  | "Full Bathroom Remodel"
  | "Walk-in Tub Installation"
  | "Window Install"
  | "Door Install"
  | "Flooring"
  | "Roofing"
  | "Painting"
  | "Plumbing"
  | "Drywall"
  | "Other";

export const PROJECT_TYPES: ProjectType[] = [
  "Shower Remodel",
  "Shower Remodel (Wet Space Only)",
  "Bathtub Remodel",
  "Bathtub Remodel (Wet Space Only)",
  "Tub to Shower Conversion",
  "Tub to Shower Conversion (Wet Space Only)",
  "Full Bathroom Remodel",
  "Walk-in Tub Installation",
  "Window Install",
  "Door Install",
  "Flooring",
  "Roofing",
  "Painting",
  "Plumbing",
  "Drywall",
  "Other",
];

export type ProjectVertical = "Bathroom Project" | "Windows" | "Doors" | "Flooring";

export type JobChecklistPhaseKey = "prep" | "demo" | "rough" | "install" | "finish" | "qa" | "custom" | "rework";

export interface JobChecklistItem {
  phaseKey: JobChecklistPhaseKey;
  phaseLabel?: string | null;
  itemIndex: number;
  label: string;
  proofLabel?: string | null;
  requiresProof: boolean;
  sortOrder?: number;
  isActive?: boolean;
  templateId?: string | null;
  templateItemId?: string | null;
}

export interface JobChecklist {
  templateKey: string;
  vertical: string;
  items: JobChecklistItem[];
  updatedAt?: string;
}

export type BathroomScopeOption =
  | "Wet Space Only"
  | "Shower Replacement"
  | "Tub to Shower Conversion"
  | "Tub Replacement"
  | "Walk-in Tub Installation"
  | "Full Bathroom Remodel";

export type BathroomPhase2Addon = "Flooring" | "Vanity" | "Toilet" | "Paint" | "Window";

export type BathroomMaterial = "Sentrel" | "Dekton" | "Tile";

export type BathroomSystemType = "Walk-in Shower" | "Standard Tub" | "Walk-in Tub" | "Jetted Tub";
export type BathroomBaseSize = "30x60" | "Custom";

// ── Hardware & Design Types ──
export type HardwareFinish = "Chrome" | "Brushed Nickel" | "Oil Rubbed Bronze" | "Matte Black" | "Gold" | "Multi";

export interface DiscountSelection {
  military: boolean;
  senior: boolean;
  frontliner: boolean;
}

export interface CustomAddOn {
  id: string;
  name: string;
  priceOverride: string;
}

// ── Expanded Project Categories ──
export type ConsultationCategory =
  | "Shower Remodel"
  | "Bath Conversion"
  | "Full Bathroom Remodel"
  | "Walk-in Tub"
  | "Windows"
  | "Doors";

export type ConsultationVertical = "bathroom" | "windows" | "doors";

export function getVerticalFromCategory(category: ConsultationCategory): ConsultationVertical {
  switch (category) {
    case "Windows": return "windows";
    case "Doors": return "doors";
    default: return "bathroom";
  }
}

export interface CustomQuoteItem {
  id: string;
  itemName: string;
  category: string;
  notes: string;
  priceAdjustment: string;
  costPlaceholder: string;
  isCustom: true;
}

// ── Site Condition Types ──
export type LayoutType = "Alcove" | "Corner" | "Custom";
export type EnclosureType = "Frameless Glass" | "Semi-Frameless Glass" | "Sliding Glass" | "Curtain Rod" | "Open" | "";

export interface WallMeasurement {
  width: string;
  height: string;
}

export interface StructuralChanges {
  removingWalls: boolean;
  addingWalls: boolean;
  movingDrain: boolean;
  movingPlumbing: boolean;
  subfloorMod: boolean;
  windowMod: "Yes" | "No" | "N/A";
}

export interface ProjectConfiguration {
  // Step 1 — Current State
  layoutType: LayoutType | "";
  wallCount: number;
  hasWindow: boolean;
  obstructions: string[];
  // Step 2 — Measurements
  wallMeasurements: {
    wallA: WallMeasurement;
    wallB: WallMeasurement;
    wallC: WallMeasurement;
    additional: WallMeasurement[];
  };
  // Step 3 — Structural Changes
  structuralChanges: StructuralChanges;
  // Step 4 — Auto-derived project type (read-only)
  autoProjectType: string;
  // Step 5 — Design
  enclosureType: EnclosureType;

  // Legacy fields kept for compatibility
  selectedVerticals: ProjectVertical[];
  bathroomScopes: BathroomScopeOption[];
  bathroomPhase2Addons: BathroomPhase2Addon[];
  bathroomMaterials: BathroomMaterial[];
  bathroomSystemType: BathroomSystemType | "";
  bathroomBaseSize: BathroomBaseSize | "";
  bathroomCustomSize: string;
  baseWidth: string;
  baseLength: string;
  baseType: string;
  baseStyle: string;
  // Complexity factors
  laborComplexity: "standard" | "moderate" | "high";
  plumbingRelocation: boolean;
  customModifications: boolean;
  // Manual adjustment
  manualAdjustment: string;
  manualAdjustmentNotes: string;
  // Core build configuration
  selectedWallSystem: string;
  selectedBase: string;
  selectedGrabBars: string[];
  selectedGlass: string;
  selectedDoor: string;
  selectedFixture: string;
  selectedAddOns: string[];
  customQuoteItems: CustomQuoteItem[];
  projectCategory: string;
  workDescription: string;
  areas: string[];
  totalSqFt: string;
  roomCount: string;
  measurementNotes: string;
  primaryMaterial: string;
  materialGrade: string;
  colorSelection: string;
  wallColor: string;
  baseColor: string;
  drainLocation: string;
  additionalOptions: string[];
  basePrice: string;
  discount: string;
  discountType: "percent" | "flat";
  // ── New consultation fields ──
  hardwareFinish: HardwareFinish | "";
  hardwareOverrides: Record<string, HardwareFinish>;
  discounts: DiscountSelection;
  customAddOns: CustomAddOn[];
  noAddOnsSelected?: boolean;
  consultationCategory: ConsultationCategory | "";
  // Full Bathroom Remodel specific
  fullBathroomSelections: {
    flooring: string;
    toilet: string;
    vanity: string;
  };
  fullBathroomAddOns: string[];
  fullBathroomCustomAddOns: CustomAddOn[];
  // Window/Door specific
  windowCount: number;
  windowTypes: string[];
  doorEntryType: string;
  doorSize: string;
  additionalCharges: string;
  additionalChargesNote: string;
  specialConditions: string;
  customerRequests: string;
  internalNotes: string;
  status: "draft" | "presented" | "accepted" | "declined";
  lastUpdated: string;
}

export type DetailedOrderStage =
  | "Not Ordered"
  | "Quote Requested"
  | "PO Submitted"
  | "Order Confirmed"
  | "In Production"
  | "Shipped"
  | "Partial Delivery"
  | "Delivered"
  | "Inspected & Staged";

export interface OrderLineItem {
  product: string;
  vendor: string;
  poNumber: string;
  category: string;
  orderDate: string;
  expectedArrival: string;
  confirmedArrival: string;
  stage: DetailedOrderStage;
  missingItems: string;
  notes: string;
  orderedBy: string;
  // Integration-ready
  externalRef?: ExternalRef;
}

export type PaymentStatus = "Not Invoiced" | "Invoiced" | "Partial Payment" | "Paid" | "Overdue" | "Deposit Requested" | "Deposit Received" | "Final Payment Pending" | "Paid in Full";
export type PayoutStatus = "Not Submitted" | "Submitted" | "Approved" | "Paid";

// ── Payment Structure Engine ──

export type PaymentPlanType = "cash" | "financed" | "card" | "mixed";
export type PaymentLineItemKind = "deposit" | "progress" | "final";
export type PaymentLineItemMethod = "cash" | "financed" | "card";
export type PaymentLineItemStatus = "pending" | "approved" | "paid" | "waived";
export type FinancingStatus = "not_applicable" | "pending" | "approved" | "declined";

/** When this obligation must be satisfied relative to the project lifecycle */
export type PaymentDueAt = "pre_schedule" | "pre_install" | "post_install";

export interface PaymentLineItem {
  id: string;
  kind: PaymentLineItemKind;
  method: PaymentLineItemMethod;
  amount: number;
  status: PaymentLineItemStatus;
  /** When this line item is due. Defaults by kind: deposit→pre_schedule, progress→pre_install, final→post_install */
  dueAt?: PaymentDueAt;
}

export interface PaymentPlan {
  type: PaymentPlanType;
  requiresDeposit: boolean;
  depositAmount: number;
  lineItems: PaymentLineItem[];
}

export interface FinancingDetails {
  financingStatus: FinancingStatus;
  financingApprovedAmount?: number;
  customerDownPaymentAmount?: number;
  financingProvider?: string;
  financingTerm?: number;
}

export type JobOtherExpenseCategory =
  | "fuel_logistics"
  | "dumpster_disposal"
  | "misc_operational";

export interface JobOtherExpense {
  id: string;
  category: JobOtherExpenseCategory;
  amount: number;
  incurredOn?: string;
  note?: string;
}

export interface JobFinancials {
  contractValue: number;
  estimatedMaterialCost: number;
  estimatedLaborCost: number;
  subcontractorPayout: number;
  marginEstimate: number;
  paymentStatus: PaymentStatus;
  payoutStatus: PayoutStatus;
  // ── Phase 1: Invoice & Payment Layer ──
  invoicePlan?: InvoicePlan;
  invoices?: Invoice[];
  payments?: Payment[];
  // ── Phase 2: Payment Structure Engine ──
  paymentPlan?: PaymentPlan;
  financing?: FinancingDetails;
  otherExpenses?: JobOtherExpense[];
}

export interface InvoicePlan {
  depositAmount: number;
  finalAmount: number;
  changeOrdersTotal?: number;
  totalContractValue: number;
}

export type InvoiceType = "deposit" | "progress" | "final";
export type InvoiceStatus = "draft" | "sent" | "paid" | "overdue";
export type PaymentMethod = "ACH" | "card" | "financing";

export interface Invoice {
  id: string;
  type: InvoiceType;
  amount: number;
  status: InvoiceStatus;
  dueDate?: string;
  issuedAt?: string;
  paidAt?: string;
  paymentMethod?: PaymentMethod;
  invoiceNumber?: string;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  method: string;
  timestamp: string;
}

export interface MaterialLineItem {
  sku: string;
  itemName: string;
  quantityRequired: number;
  quantityAllocated: number;
  quantityStaged: number;
  quantityInstalled: number;
  missingItems: number;
  fieldPurchase: boolean;
  inventoryStatus: InventoryItemStatus;
  /** Assigned = purchased for this job; Stock = pulled from general inventory */
  inventoryAssignment: InventoryAssignmentType;
  /** Material lifecycle tracking */
  lifecycleStage: MaterialLifecycleStage;
  /** Unit cost (COGS) - populated from product_registry.cost or job_materials.unit_cost_snapshot */
  unitCost?: number;
  /** Optional sales tax percentage applied to this core material line. */
  taxPercent?: number;
  /** Unit price (selling) - populated from product_registry.price or job_materials.unit_price_snapshot */
  unitPrice?: number;
  // Integration-ready
  externalRef?: ExternalRef;
  externalItemId?: string;
}

export interface FieldMaterialReceiptReference {
  attachmentId: string;
  fileName?: string;
  mimeType?: string;
  createdAt?: string;
  invoiceNumber?: string;
  vendorName?: string;
  purchasedAt?: string;
  tax?: number;
  total?: number;
}

export interface FieldMaterialItem {
  id: string;
  itemName: string;
  sku?: string;
  quantity: number;
  unitCost?: number;
  vendorName?: string;
  purchasedAt?: string;
  notes?: string;
  receipts?: FieldMaterialReceiptReference[];
  /** Backward-compatible Koredor Website shape before receipts[] alignment. */
  receiptAttachmentIds?: string[];
  /** Backward-compatible Koredor Website shape before receipts[] alignment. */
  receiptFileNames?: string[];
  fieldPurchaseLogId?: string;
  inventoryPostedAt?: string;
  inventoryPostedBy?: string;
}

// ── Material Package (output of Prepare Materials wizard) ──
export interface MaterialPackageItem {
  categorySlug: string;
  categoryLabel: string;
  productId: string;
  productName: string;
  quantity: number;
  skus: string[];
}

export interface MaterialPackage {
  id: string;
  jobId: string;
  createdAt: string;
  createdBy: string;
  items: MaterialPackageItem[];
  addOns: MaterialPackageItem[];
  status: "prepared" | "ordered";
}

export interface MarketingAttribution {
  leadSource: string;
  campaignName: string;
  aggregatorPlatform: string;
  callCenterRep: string;
  appointmentDate: string;
  saleOutcome: string;
  /** Cost to acquire this lead */
  leadCost?: number;
  /** Campaign identifier for attribution */
  campaign?: string;
}

export type IssueBlockerCategory =
  | "Missing Material"
  | "Permit Rejection"
  | "HOA Documentation"
  | "Crew Unavailable"
  | "Customer Unavailable"
  | "Damaged Material"
  | "Design Error"
  | "Scheduling Conflict"
  | "Weather Delay"
  | "Supplier Delay"
  | "Payment Hold"
  | "Inspection Failure"
  | "Pending Identification"
  | "Other";

export type IssueAffectedArea =
  | "Shower Base"
  | "Glass Panel"
  | "Glass Door"
  | "Plumbing"
  | "Tile"
  | "Fixtures"
  | "Vanity"
  | "Framing"
  | "Waterproofing"
  | "Electrical"
  | "General"
  | "Scheduling"
  | "Materials"
  | "Permit"
  | "Other";

export interface Issue {
  id: string;
  jobId: string;
  issueType: IssueType;
  severity: IssueSeverity;
  description: string;
  photo: string;
  owner: string;
  status: IssueStatus;
  dateCreated: string;
  source: IssueSource;
  relatedContractorId?: string;
  resolutionNotes?: string;
  dateInProgress?: string;
  dateResolved?: string;
  dateClosed?: string;
  // Structured intake fields
  blockerCategory?: IssueBlockerCategory;
  blockerDetail?: string;
  homeownerConcern?: string;
  affectedArea?: IssueAffectedArea;
  suggestedAction?: string;
  jobStageAtCreation?: string;
  // Integration-ready
  externalRef?: ExternalRef;
}

export type AvailabilityStatus = "Available" | "Unavailable" | "Limited";

export type OnboardingStatus =
  | "Lead"
  | "Invited"
  | "Application Started"
  | "Under Review"
  | "Approved"
  | "Rejected"
  | "Inactive";

export interface Contractor {
  id: string;
  organizationId?: string;
  /** Existing contractor hierarchy discriminator from contractors.contractor_type. */
  contractorType?: "main_parent" | "sub_parent" | null;
  companyName: string;
  contactName: string;
  /** P10 — Optional first name for personalized greetings (Daily Anchor). */
  contactFirstName?: string;
  phone: string;
  email: string;
  serviceArea: string;
  serviceRegions: string[];
  tradeType: string;
  active: boolean;
  rating: number;
  responsivenessRating: number;
  notes: string;
  // Compliance & insurance
  insuranceOnFile: boolean;
  insuranceExpiry: string;
  w9OnFile: boolean;
  licenseNumber: string;
  complianceNotes: string;
  // Onboarding
  onboardingStatus: OnboardingStatus;
  agreementSigned: boolean;
  tags: string[];
  branch: string;
  // Stats
  joinedDate: string;
  // Availability & capacity
  availabilityStatus: AvailabilityStatus;
  maxWeeklyJobs: number;
  serviceZipCodes: string[];
  /**
   * Declarative availability state (Availability Orchestration Layer, Phase A).
   * Source of truth for PTO / paused / limited. `at_capacity` is DERIVED —
   * never persisted here. Resolve via `getContractorAvailability()`.
   * Anchor: src/data/contractor/availability.ts
   */
  availability?: import("./contractor/availability").AvailabilityState;
  availabilityDeclaredAt?: string;
  availabilityDeclaredBy?: string;
  // Marketplace (P0 — derived eligibility, see resolveContractorEligibility)
  marketplaceOptIn?: boolean;
  marketplaceAdminOverride?: {
    decision: "allow" | "deny";
    reason: string;
    setBy: string;
    setAt: string;
  } | null;
  // P5 — Contractor portal onboarding (single mutation flag, see contractorOnboarding.ts)
  onboardingCompletedAt?: string;
  // Integration-ready
  externalRef?: ExternalRef;
  // ── Phase 1a — Network Execution Truth Layer (additive) ──
  /**
   * Business role discriminator.
   *   "owner"    — Project Owner only (sells/owns the job, doesn't execute)
   *   "executor" — Installer only (performs work for an owner)
   *   "both"     — CCC and similar operators that own AND execute
   * Default at backfill time: "executor". CCC seeded as "both".
   * Anchor: mem://architecture/network-execution-phase-1a
   */
  businessRole?: "owner" | "executor" | "both";
  /**
   * Vetting state. Hard gate to "approved" enforced in Phase 1b
   * (`promoteToApproved()`). Backfill default for active veterans
   * with offer/job history is "approved"; otherwise "trial".
   */
  vettingStatus?: "trial" | "approved" | "blocked";
  /**
   * Probation flag — the only stored "blocked" state. Derived execution
   * eligibility goes through `isContractorExecutionBlocked()` in 1b.
   */
  isOnProbation?: boolean;
  /**
   * Cumulative completed-jobs counter. Updated by completion handlers in
   * Phase 1b. Backfilled in 1a from existing `Job.currentStage === "Completed"`.
   */
  jobsCompleted?: number;
  /**
   * Vetting evidence. License/insurance/photos/skill-checklist. Hard
   * promotion gate consults all four in Phase 1b.
   */
  vetting?: {
    licenseVerified: boolean;
    insuranceVerified: boolean;
    submittedJobPhotos: Array<{ url: string; jobType: string; uploadedAt: string }>;
    skillChecklistCompleted: boolean;
    verifiedAt?: string;
    /** Manual approver id captured by promoteToApproved() (Phase 1b). */
    approvedBy?: string;
    /** Manual approval justification (≥10 chars). */
    approvalReason?: string;
    /** ISO timestamp of the manual approval write. */
    approvedAt?: string;
  };
  /**
   * EN-1 — Additive entity classification layer.
   * ─────────────────────────────────────────────
   * STRUCTURAL ONLY. No consumer may branch on these fields until
   * EN-2 lands `resolveExecutionResponsibilities()`. See
   * `src/data/contractor/networkExecution/RECONCILIATION.md` §9.
   *
   * Defaults at backfill:
   *   entityType         = "independent"
   *   parentEntityId     = null
   *   relationshipType   = null
   *   accountabilityMode = "self"
   * CCC is the only seeded exception (`entityType="internal_w2"`).
   */
  entityType?:
    | "independent"
    | "licensed_contractor"
    | "internal_w2"
    | "branch"
    | "crew"
    | "helper"
    | "manufacturer_partner";
  parentEntityId?: string | null;
  relationshipType?:
    | "child_of"
    | "branch_of"
    | "crew_of"
    | "helper_of"
    | "certified_under"
    | null;
  accountabilityMode?: "self" | "parent" | "hybrid";
  /**
   * IN-1.c — Agreement signatures (append-only).
   * Latest signature whose `variant` matches
   * the entity-type requirements matrix (IN-1.a) satisfies the
   * agreement requirement. No destructive overwrite. Observation-only.
   * Anchor: RECONCILIATION.md §14 (IN-1.c)
   */
  agreementSignatures?: Array<{
    id: string;
    variant:
      | "1099_msa"
      | "w2_offer_letter"
      | "branch_sublicense"
      | "crew_acknowledgment"
      | "helper_acknowledgment"
      | "certification_agreement";
    version: string;
    signedAt: string;
    signedBy: string;
    ipAddress?: string;
  }>;
  /**
   * IN-1.c — Payout method (no processor integration).
   * NEVER stores ACH/routing numbers — `last4` is the only sensitive
   * surface allowed. `parent_routed` requires `parentEntityId`.
   */
  payoutMethod?: {
    type: "ach" | "check" | "stripe_connect" | "parent_routed" | "payroll";
    verified: boolean;
    verifiedAt?: string;
    last4?: string;
    parentEntityId?: string;
  };
  /**
   * IN-1.d — Append-only suspension audit log. Mutated only by
   * `suspendContractor` / `reactivateContractor`. Never trimmed.
   * Anchor: RECONCILIATION.md §15 (IN-1.d)
   */
  suspensionLog?: Array<{
    id: string;
    action: "suspend" | "reactivate";
    reason: string;
    actor: string;
    at: string;
    priorStatus: "trial" | "approved" | "blocked";
  }>;
}


export interface ContractorOpportunity {
  id: string;
  jobId: string;
  contractorId: string;
  sentDate: string;
  viewedDate: string;
  responseDate: string;
  responseStatus: ContractorResponseStatus;
  bidAmount: number | null;
  bidNotes: string;
  isWinner: boolean;
  rejectionReason?: string;
  acceptedDate?: string;
  rejectedDate?: string;
}

export type NoteType =
  | "ops"
  | "customer"
  | "crew"
  | "subcontractor"
  | "permit"
  | "ordering"
  | "issue"
  | "system";

export interface ActivityEntry {
  id: string;
  date: string;
  action: string;
  user: string;
  noteType?: NoteType;
}

export type CommType =
  | "Phone Call"
  | "Text"
  | "Email"
  | "Appointment Confirmation"
  | "Issue Follow-up"
  | "Service Update"
  | "Internal Note";

export type CommSource = "Manual" | "GHL" | "External";

export interface CustomerCommEntry {
  id: string;
  date: string;
  time: string;
  commType: CommType;
  summary: string;
  relatedJobId?: string;
  user: string;
  source: CommSource;
  // Integration-ready
  externalRef?: ExternalRef;
  origin?: DataOrigin;
}

// ── Customer Credits & Resolutions ──
export type CreditType =
  | "Goodwill Credit"
  | "Service Recovery Credit"
  | "Material / Damage Credit"
  | "Install Issue Credit"
  | "Pricing Adjustment"
  | "Other";

export type CreditStatus =
  | "Draft"
  | "Proposed"
  | "Approved"
  | "Offered to Customer"
  | "Accepted"
  | "Applied"
  | "Declined"
  | "Canceled";

export type CreditReasonCategory =
  | "Quality Issue"
  | "Scheduling Delay"
  | "Material Defect"
  | "Customer Complaint"
  | "Install Error"
  | "Pricing Error"
  | "Service Failure"
  | "Other";

export type CreditAttributionType =
  | "Crew-Related"
  | "Material-Related"
  | "Customer Goodwill"
  | "Vendor-Related"
  | "Unknown";

export interface CustomerCredit {
  id: string;
  customerId: string;
  jobId: string;
  issueId?: string;
  relatedContractorId?: string;
  relatedCrewName?: string;
  creditAmount: number;
  creditType: CreditType;
  reasonCategory: CreditReasonCategory;
  attributionType: CreditAttributionType;
  notes: string;
  status: CreditStatus;
  dateCreated: string;
  dateOffered?: string;
  createdBy: string;
  approvedBy?: string;
}
